import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { VideoPlayerSyncContext } from './VideoPlayerSyncContext';
import { FileUploadTypeEnum } from '../../generated/api';
import { React } from '../../Imports';

interface VideoPlayerSyncProviderProps {
    children: React.ReactNode;
    isMobileView: boolean;
}

const VideoPlayerSyncProvider = (props: VideoPlayerSyncProviderProps) => {
    const [isPlayingGlobal, setIsPlayingGlobal] = useState<boolean>(false);
    const [maxDurationGlobal, setMaxDurationGlobal] = useState<number>(0);
    const [seekTimeGlobal, setSeekTimeGlobal] = useState<number>(0);

    const [isMobileView, setIsMobileView] = useState<boolean>(props.isMobileView ?? false);
    const [videosStarted, setVideosStarted] = useState<boolean>(false);
    const [videosWatched, setVideosWatched] = useState<boolean>(false);
    const [maxDurationVideoType, setMaxDurationVideoType] = useState<FileUploadTypeEnum>('FrontVideo');
    const [videosEnded, setVideosEnded] = useState<{ videoType: FileUploadTypeEnum; ended: boolean }[]>([]);
    const [startPlayFromBeginning, setStartPlayFromBeginning] = useState<boolean>(false);

    const calculateMaxDuration = (duration: number, videoType: FileUploadTypeEnum) => {
        if (duration > maxDurationGlobal) {
            setMaxDurationGlobal(duration);
            setMaxDurationVideoType(videoType);
        }
    };

    const createSyncedSetter = useCallback(
        <T,>(setter: Dispatch<SetStateAction<T>>) => {
            return (value: SetStateAction<T>) => {
                // sync only when not in full view to prevent unwanted syncing during fullscreen playback
                if (!isMobileView) {
                    setter(value);
                }
            };
        },
        [isMobileView],
    );

    const setVideoEnded = (videoType: FileUploadTypeEnum, ended: boolean) => {
        setVideosEnded((prev) => {
            const existingIndex = prev.findIndex((v) => v.videoType === videoType);
            if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = { videoType, ended };
                return updated;
            }
            return [...prev, { videoType, ended }];
        });
    };

    const allVideosEnded = (): boolean => {
        return videosEnded.length > 0 && videosEnded.every((v) => v.ended);
    };

    return (
        <VideoPlayerSyncContext.Provider
            value={{
                isPlayingGlobal,
                setIsPlayingGlobal: createSyncedSetter(setIsPlayingGlobal),
                seekTimeGlobal,
                setSeekTimeGlobal: createSyncedSetter(setSeekTimeGlobal),
                maxDurationGlobal,
                setMaxDurationGlobal: calculateMaxDuration,
                maxDurationVideoType,
                isMobileView,
                setIsMobileView,
                videosStarted,
                setVideosStarted,
                videosWatched,
                setVideosWatched,
                videosEnded,
                setVideoEnded,
                allVideosEnded,
                startPlayFromBeginning,
                setStartPlayFromBeginning,
            }}
        >
            {props.children}
        </VideoPlayerSyncContext.Provider>
    );
};

export default VideoPlayerSyncProvider;
